import type { QueryPlan } from './plan';
import type { TableData } from './files';

type RetrievePlan = Extract<QueryPlan, { kind: 'retrieve' }>;

export type UiSnapshot = {
  route: string | null;
  businessSites: { code: string; name: string; current: boolean }[];
  fields: string[];
  actions: string[];
  arrays: { key: string; rowCount: number; columns: string[] }[];
};

export type ValidationIssue = {
  label: string;
  /**
   * `no-answer` 는 **`empty-result` 와 반드시 갈라야 한다.** 둘 다 0행으로 보이지만
   * 뜻이 정반대다 — 하나는 「그 조건에 자료가 없다」는 **사실**이고, 다른 하나는
   * 「우리가 아직 모른다」는 것이다. 섞으면 사람에게 없는 사실을 단언하게 된다.
   */
  code: 'empty-result' | 'missing-column' | 'empty-column' | 'no-answer';
  message: string;
};

export type ValidationReport = { valid: boolean; issues: ValidationIssue[] };

export type ExperienceCandidate = {
  version: 1;
  question: string;
  requirements?: RetrievePlan['requirements'];
  recipe: {
    route: string;
    rowsStateKey: string;
    conditionShape: Record<string, string>;
    requiredColumns: string[];
  }[];
  snapshots: UiSnapshot[];
  validation: ValidationReport;
  artifact: { kind: 'none' | 'table'; rows: number; columns: number };
  createdAt: number;
};

type Retrieved = {
  /**
   * 그 조회의 이름. 여기서는 안 쓰지만 **부르는 쪽이 담아서 넘긴다**
   * (`index.ts` 의 `Retrieved`). 안 적어 두면 시험이 실제 모양을 못 쓴다.
   */
  label?: string;
  rows: Record<string, unknown>[];
  totals?: Record<string, unknown>;
  snapshot?: UiSnapshot;
  /** 드라이버가 채운다. 거짓이면 서버가 대답을 안 했고 0행은 결과가 아니다. */
  answered?: boolean;
  /** 펼쳐 읽기로 받았으면 위 표 행 수와 읽은 행 수, 다 못 읽은 까닭. */
  swept?: { masterRows: number; read: number; missed?: number; unchanged?: number; reason?: string };
};

export type RetrievalOutcome = {
  kind: 'success' | 'legitimate-empty' | 'wrong-grid' | 'missing-column' | 'empty-column'
    | 'condition-rejected' | 'permission-denied' | 'route-mismatch' | 'no-answer';
  label: string;
  message: string;
};

function present(value: unknown): boolean {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

/**
 * 행에 **키조차 없는** 필수 칸.
 *
 * 필수 칸은 계획 단계에서 카탈로그 표와 이미 대조됐다(`parsePlan`). 그러니 실행 뒤에
 * 키가 없는 것은 **서버가 값 없는 키를 빼고 보낸 것**이다 — 비용항목등록 상세 8행에
 * `gflag` 가 하나도 없었고, 템플릿은 `x.gflag` 를 읽는다(2026-09-23 실측). 그것을
 * 「칸 없음」으로 읽으면 멀쩡한 표를 버리고 다른 표를 찾아 다시 계획한다.
 *
 * 다만 **전부** 없으면 엉뚱한 표를 읽은 것이다. 부르는 쪽이 그것만 칸 없음으로 본다.
 */
function absentColumns(required: string[], rows: Record<string, unknown>[]): string[] {
  return required.filter((column) => !rows.some((row) => column in row));
}

/** 빈 배열 하나를 곧바로 "데이터 없음"으로 부르지 않고 다음 행동에 필요한 원인으로 분류한다. */
export function classifyRetrieved(
  retrieval: RetrievePlan['retrievals'][number],
  result: Retrieved | undefined,
  /**
   * 자유 기재 칸(concept `note`). 다 비어도 자료가 그런 것이다 — 중대결함 99건이 모두 미처리라 처리여부상세가
   * 비었는데 「필수 칸이 비었다」며 같은 화면을 두 번 다시 계획했다(실제 테넌트, 2026-10-22).
   */
  freeText: string[] = [],
): RetrievalOutcome {
  const cleanLabel = retrieval.label.replace(/__(?:(?:retry|settled)\d+|wide)$/, '');
  const notices = (result as Retrieved & { notices?: string[] } | undefined)?.notices ?? [];
  const notice = notices.join(' ');
  if (/권한|접근.*거부|permission/i.test(notice)) {
    return { kind: 'permission-denied', label: cleanLabel, message: '조회 권한이 확인되지 않았다' };
  }
  if (/선택되지|입력.*필수|조회.*조건|선행조건/i.test(notice)) {
    return { kind: 'condition-rejected', label: cleanLabel, message: '조회 조건이 화면에서 거부됐다' };
  }
  if (result?.snapshot?.route && result.snapshot.route !== retrieval.route) {
    return { kind: 'route-mismatch', label: cleanLabel, message: '요청한 화면이 열리지 않았다' };
  }
  // **서버가 대답을 안 했으면 0행은 결과가 아니다**(`validateRetrieved`와 같은 판정). 「결과 행이 없었다」로
  // 읽으면 다시 조회하지 않고 「자료 사정」으로 빠진다. 실제 테넌트(2026-10-26 04:52)에서 10월 점검결과 조회가
  // 그렇게 실패했고, 6분 뒤 같은 조회는 28초에 2,348행을 받았다.
  if (result?.answered === false) {
    return { kind: 'no-answer', label: cleanLabel, message: '화면이 서버 응답을 못 받았다' };
  }
  const rows = result?.rows ?? [];
  const hasTotal = Object.values(result?.totals ?? {}).some(present);
  // 컬럼은 행에만 있다. 행이 없는데 컬럼을 재면 필수 컬럼이 전부 "없다" 로 나온다 —
  // 고장다발현장은 0행이어도 합계를 돌려주기 때문에 그 함정에 빠졌다.
  if (!rows.length) {
    // 대상을 다 펼쳐 읽었는데 상세가 없으면 그것이 답이다 — 입사자 8명 모두 경력이 없다. 「결과 없음」으로
    // 보면 다른 화면을 찾아 다시 계획한다(개발 테넌트, 2026-10-21).
    const swept = result?.swept;
    // 행을 못 골랐거나 상세가 끝내 안 바뀐 행이 있으면 다 읽은 것이 아니다(검토, 2026-10-21).
    if (swept && swept.read > 0 && swept.read >= swept.masterRows && !swept.reason
      && !swept.missed && !swept.unchanged) {
      return { kind: 'success', label: cleanLabel, message: `위 목록 ${swept.masterRows}건을 다 펼쳐 읽었고 상세가 없었다` };
    }
    const arrays = result?.snapshot?.arrays;
    if (!hasTotal && arrays?.length && !arrays.some((array) => array.key === retrieval.rowsStateKey)) {
      return { kind: 'wrong-grid', label: cleanLabel, message: `결과 표 ${retrieval.rowsStateKey}를 찾지 못했다` };
    }
    return { kind: 'legitimate-empty', label: cleanLabel, message: '조회는 끝났지만 결과 행이 없었다' };
  }
  const required = retrieval.requiredColumns ?? [];
  const absent = absentColumns(required, rows);
  // 필수 칸이 **전부** 없으면 엉뚱한 표를 읽은 것이다. 일부만 없으면 서버가 값 없는
  // 키를 뺀 것이므로 아래에서 「값 없음」으로 본다(`absentColumns`).
  if (required.length && absent.length === required.length) {
    return { kind: 'missing-column', label: cleanLabel, message: `필수 컬럼 ${absent[0]}이 없다` };
  }
  for (const column of required) {
    if (freeText.indexOf(column) < 0 && !rows.some((row) => present(row[column]))) {
      return { kind: 'empty-column', label: cleanLabel, message: `필수 컬럼 ${column} 값이 비어 있다` };
    }
  }
  return { kind: 'success', label: cleanLabel, message: '데이터 계약을 만족한다' };
}

/** 계산 전에 원천 행이 계획의 데이터 계약을 실제로 만족하는지 확인한다. */
export function validateRetrieved(
  plan: RetrievePlan,
  retrieved: Record<string, Retrieved>,
): ValidationReport {
  const issues: ValidationIssue[] = [];
  for (const retrieval of plan.retrievals) {
    const result = retrieved[retrieval.label];
    const rows = result?.rows ?? [];
    const hasTotal = Object.values(result?.totals ?? {}).some(present);
    // **서버가 대답을 안 했으면 0행은 결과가 아니다.** 화면은 조회를 누를 때 표를
    // 먼저 비우므로, 응답이 안 온 채 마감이 지나면 빈 배열이 남는다. 그것을
    // `empty-result` 로 읽으면 「조건에 맞는 자료가 없습니다」라고 **단언**하게 된다 —
    // 사람은 그 말을 믿고 일한다. 합계가 있어도 마찬가지다: 그것도 옛 값일 수 있다.
    if (result && result.answered === false) {
      issues.push({
        label: retrieval.label,
        code: 'no-answer',
        message: `${retrieval.label} 조회에 화면이 서버 응답을 못 받았다`,
      });
      continue;
    }
    // 행이 없으면 컬럼 검사를 하지 않는다. 합계만 돌려주는 화면에서 필수 컬럼이
    // 통째로 "없다" 로 보고돼 자료가 없다는 사실이 가려졌다.
    if (!rows.length) {
      // 합계만 있는 것을 결과로 인정하되, 컬럼을 요구한 조회는 행이 있어야 한다.
      if (!hasTotal || (retrieval.requiredColumns?.length ?? 0) > 0) {
        issues.push({
          label: retrieval.label,
          code: 'empty-result',
          message: `${retrieval.label} 조회 결과가 비어 있다`,
        });
      }
      continue;
    }
    const blank: string[] = [];
    const wanted = retrieval.requiredColumns ?? [];
    const absent = absentColumns(wanted, rows);
    const wrongTable = wanted.length > 0 && absent.length === wanted.length;
    for (const column of wanted) {
      if (wrongTable) {
        issues.push({
          label: retrieval.label,
          code: 'missing-column',
          message: `${retrieval.label} 결과에 필수 컬럼 ${column} 이 없다`,
        });
      } else if (!rows.some((row) => present(row[column]))) {
        // 키가 아예 없는 칸도 여기로 온다 — 값 없음이다(`absentColumns`).
        blank.push(column);
      }
    }
    // **빈 칸이 다 같은 무게는 아니다.** 금액·수량이 비면 답이 안 되지만, 결재 전
    // 문서의 `appgubun`(결재상태)이 빈 것은 정상이다. 그것 하나로 민원 조회 두 행이
    // 다 버려졌고 사용자는 `조건에 맞는 자료가 없습니다` 만 봤다 — 화면에는 멀쩡히 있었다.
    const measures = blank.filter((column) => /(amt|qty|cnt|price|amount)/i.test(column));
    const fatal = measures.length ? measures : (blank.length === wanted.length ? blank : []);
    if (fatal.length) {
      issues.push({
        label: retrieval.label,
        code: 'empty-column',
        message: `${retrieval.label} 결과의 필수 컬럼 ${fatal.join(', ')} 값이 모두 비어 있다`,
      });
    }
  }
  return { valid: issues.length === 0, issues };
}

function conditionShape(key: string, value: string): string {
  if (/perid|userid|cust|tel|name/i.test(key)) return '{current-user}';
  if (/^20\d{2}$/.test(value)) return '{year}';
  if (/^20\d{4}$/.test(value)) return '{year-month}';
  if (/^20\d{6}$/.test(value) || /^20\d{2}-\d{2}-\d{2}$/.test(value)) return '{date}';
  if (/^(?:0|1|%|ZZ)$/.test(value)) return value;
  return '{selected-value}';
}

/** 행 값 없이 재사용 가능한 실행 레시피와 화면 구조만 경험 후보로 만든다. */
export function makeExperience(
  question: string,
  plan: RetrievePlan,
  retrieved: Record<string, Retrieved>,
  validation: ValidationReport,
  table?: TableData,
): ExperienceCandidate {
  return {
    version: 1,
    question,
    requirements: plan.requirements,
    recipe: plan.retrievals.map((retrieval) => ({
      route: retrieval.route,
      rowsStateKey: retrieval.rowsStateKey,
      conditionShape: Object.fromEntries(Object.entries(retrieval.conditions)
        .map(([key, value]) => [key, conditionShape(key, value)])),
      requiredColumns: retrieval.requiredColumns ?? [],
    })),
    snapshots: plan.retrievals.flatMap((retrieval) => {
      const snapshot = retrieved[retrieval.label]?.snapshot;
      return snapshot ? [snapshot] : [];
    }),
    validation,
    artifact: table
      ? { kind: 'table', rows: table.rows.length, columns: table.columns.length }
      : { kind: 'none', rows: 0, columns: 0 },
    createdAt: Date.now(),
  };
}

export class ExecutionValidationError extends Error {
  constructor(readonly report: ValidationReport) {
    super(`조회 결과 검증 실패: ${report.issues.map((issue) => (
      issue.message.replace(/__retry\d+/g, '')
    )).join(' / ')}`);
    this.name = 'ExecutionValidationError';
  }
}
